import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { getCountryNameKR } from "../constants/countryNames";
import { buildGenderRatio, getCountryFlagSvg } from "../utils/brandWikiUtils";

import IconClose from "../../../assets/icon_close.svg";
import "./BrandWiki.css";

/**
 * 브랜드 위키 팝업. 국가, 도시, 설립일, 성별 비율 정보를 보여준다.
 * - PC(768px 이상): 화면 중앙 다이얼로그
 * - 모바일(768px 미만): 바텀시트
 * @param brand 브랜드 (brand_metadata 포함)
 * @param onClose 닫기 콜백
 */
export default function BrandWikiModal({ brand, onClose }) {
  const metadata = brand.brand_metadata || {};
  const countryName = getCountryNameKR(metadata.country_iso_code);
  const flagSvg = getCountryFlagSvg(metadata.country_iso_code);
  const ratio = buildGenderRatio(metadata.male_ratio, metadata.female_ratio);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="BrandWikiModal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${brand.name} 브랜드 위키`}
    >
      <div className="BrandWikiModal" onClick={(e) => e.stopPropagation()}>
        <div className="BrandWikiModal-header">
          <h3 className="BrandWikiModal-title">{brand.name}</h3>
          <button
            type="button"
            className="BrandWikiModal-close"
            onClick={onClose}
          >
            <img src={IconClose} alt="닫기" width={24} height={24} />
          </button>
        </div>
        <div className="BrandWikiModal-content">
          <div className="BrandWikiModal-section-label">정보</div>
          {countryName && (
            <div className="BrandWikiModal-row">
              <span className="BrandWikiModal-row-key">국가</span>
              <span className="BrandWikiModal-row-value">
                {flagSvg && (
                  <span
                    className="BrandWiki-flag"
                    dangerouslySetInnerHTML={{ __html: flagSvg }}
                  />
                )}
                <span>{countryName}</span>
              </span>
            </div>
          )}
          {metadata.city && (
            <div className="BrandWikiModal-row">
              <span className="BrandWikiModal-row-key">도시</span>
              <span className="BrandWikiModal-row-value">{metadata.city}</span>
            </div>
          )}
          {metadata.founded_at && (
            <div className="BrandWikiModal-row">
              <span className="BrandWikiModal-row-key">설립일</span>
              <span className="BrandWikiModal-row-value">
                {metadata.founded_at}년
              </span>
            </div>
          )}
          {ratio && (
            <div className="BrandWikiModal-gender">
              <div className="BrandWikiModal-row-key BrandWikiModal-gender-title">
                성별
              </div>
              <div className="BrandWiki-gender-bar">
                {ratio.normalizedMaleRatio > 0 && (
                  <div
                    className="BrandWiki-gender-bar-male"
                    style={{ flex: ratio.normalizedMaleRatio }}
                  />
                )}
                {ratio.normalizedFemaleRatio > 0 && (
                  <div
                    className="BrandWiki-gender-bar-female"
                    style={{ flex: ratio.normalizedFemaleRatio }}
                  />
                )}
              </div>
              <div className="BrandWiki-gender-labels">
                <span>
                  남성 <strong>{ratio.malePercent}%</strong>
                </span>
                <span>
                  여성 <strong>{ratio.femalePercent}%</strong>
                </span>
              </div>
            </div>
          )}
          <p className="BrandWiki-disclaimer">
            등록된 정보는 실제와 다를 수 있어요.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

BrandWikiModal.propTypes = {
  brand: PropTypes.shape({
    name: PropTypes.string.isRequired,
    brand_metadata: PropTypes.shape({
      country_iso_code: PropTypes.string,
      city: PropTypes.string,
      founded_at: PropTypes.number,
      male_ratio: PropTypes.number,
      female_ratio: PropTypes.number,
    }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};
